// AI文書生成モード — SEN249 docgenパイプラインでPDF/Wordを生成（チャットとは別ルート）
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FilePdf, FileDoc, Sparkle, CircleNotch, ArrowsClockwise, Trash,
  DownloadSimple, CaretDown, CaretUp, CheckCircle, XCircle, Warning,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  queued: ['処理待ち', 'bg-zinc-100 text-zinc-600 border-zinc-200'],
  processing: ['生成中', 'bg-violet-50 text-violet-700 border-violet-200'],
  completed: ['完了', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  partially_completed: ['一部完了', 'bg-amber-50 text-amber-700 border-amber-200'],
  failed: ['失敗', 'bg-rose-50 text-rose-700 border-rose-200'],
  cancelled: ['キャンセル', 'bg-zinc-100 text-zinc-500 border-zinc-200'],
  expired: ['期限切れ', 'bg-zinc-100 text-zinc-500 border-zinc-200'],
};
const FMT_OPTS = [
  { v: ['pdf'], label: 'PDF' },
  { v: ['docx'], label: 'Word' },
  { v: ['pdf', 'docx'], label: '両方' },
];
const LANG_OPTS = [
  { v: 'auto', label: '自動' }, { v: 'en', label: 'English' },
  { v: 'ja', label: '日本語' }, { v: 'bilingual', label: '英日併記' },
];
const fmtWhen = (iso) => {
  try { const d = new Date(iso); return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; } catch (_e) { return iso; }
};

function JobCard({ job, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const active = job.status === 'queued' || job.status === 'processing';
  const [label, cls] = STATUS_BADGE[job.status] || STATUS_BADGE.queued;

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await axios.get(`${API}/ai-docs/jobs/${job.id}/download/${fmt}`, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(job.title || 'document').slice(0, 60)}.${fmt}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (_e) { toast.error('ダウンロードに失敗しました'); }
    finally { setBusy(''); }
  };
  const regen = async () => {
    setBusy('regen');
    try { await axios.post(`${API}/ai-docs/jobs/${job.id}/regenerate`, null, { withCredentials: true }); toast.success('再生成を開始しました'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || '再生成に失敗しました'); }
    finally { setBusy(''); }
  };
  const del = async () => {
    if (!window.confirm('この生成文書を削除しますか？')) return;
    try { await axios.delete(`${API}/ai-docs/jobs/${job.id}`, { withCredentials: true }); onChanged(); }
    catch (_e) { toast.error('削除に失敗しました'); }
  };

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3" data-testid={`docgen-job-${job.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12.5px] font-bold text-zinc-800 truncate max-w-[220px]">{job.title || '無題の文書'}</span>
        <span className={`text-[10px] font-semibold rounded-full border px-2 py-0.5 inline-flex items-center gap-1 ${cls}`} data-testid={`docgen-status-${job.id}`}>
          {active && <CircleNotch size={10} className="animate-spin" />}
          {job.status === 'completed' && <CheckCircle size={10} weight="fill" />}
          {job.status === 'failed' && <XCircle size={10} weight="fill" />}
          {label}
        </span>
        <span className="text-[10px] text-zinc-400 ml-auto">{fmtWhen(job.created_at)}</span>
      </div>
      {active && (
        <div className="mt-2 h-1.5 rounded-full bg-violet-100 overflow-hidden">
          <div className="h-full w-1/3 rounded-full bg-teal-700 animate-pulse" style={{ animation: 'docgen-slide 1.6s ease-in-out infinite' }} />
          <style>{'@keyframes docgen-slide{0%{margin-left:-35%}100%{margin-left:100%}}'}</style>
        </div>
      )}
      {job.safe_failure_message && (
        <p className="mt-1.5 text-[11px] text-rose-600 flex items-center gap-1"><Warning size={12} /> {job.safe_failure_message}</p>
      )}
      {(job.files || []).some((f) => f.failed) && job.status === 'partially_completed' && (
        <p className="mt-1.5 text-[11px] text-amber-700">
          {(job.files || []).filter((f) => f.failed).map((f) => f.format.toUpperCase()).join('・')} の生成に失敗しました（成功したファイルはダウンロード可能です）
        </p>
      )}
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {(job.files || []).filter((f) => !f.failed).map((f) => (
          <button key={f.format} type="button" onClick={() => download(f.format)} disabled={busy === f.format}
            data-testid={`docgen-download-${f.format}-${job.id}`}
            className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
            {busy === f.format ? <CircleNotch size={12} className="animate-spin" />
              : f.format === 'pdf' ? <FilePdf size={12} weight="fill" /> : <FileDoc size={12} weight="fill" />}
            {f.format === 'pdf' ? 'PDF' : 'Word'} <DownloadSimple size={11} />
          </button>
        ))}
        {!active && (
          <button type="button" onClick={regen} disabled={busy === 'regen'} data-testid={`docgen-regen-${job.id}`}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-50 disabled:opacity-50">
            <ArrowsClockwise size={11} /> 再生成
          </button>
        )}
        {!active && (
          <button type="button" onClick={del} data-testid={`docgen-delete-${job.id}`}
            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-[11px] text-rose-600 hover:bg-rose-50">
            <Trash size={11} />
          </button>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`docgen-details-${job.id}`}
          className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-zinc-400 hover:text-zinc-600">
          詳細 {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
        </button>
      </div>
      {open && (
        <div className="mt-2 rounded-lg bg-zinc-50 border border-zinc-100 p-2 text-[10.5px] text-zinc-500 space-y-0.5" data-testid={`docgen-detail-body-${job.id}`}>
          <p>モデル: <span className="font-mono">{job.model_id_used || '—'}</span></p>
          <p>形式: {(job.formats || []).join(', ')} ／ 言語: {LANG_OPTS.find((l) => l.v === job.language)?.label || job.language} ／ 図: {job.include_diagrams ? 'あり' : 'なし'}</p>
          <p className="line-clamp-2">プロンプト: {job.prompt}</p>
        </div>
      )}
    </div>
  );
}

export default function DocGenPanel() {
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [formats, setFormats] = useState(['pdf']);
  const [lang, setLang] = useState('auto');
  const [diagrams, setDiagrams] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ai-docs/jobs`, { withCredentials: true });
      setJobs(r.data.jobs || []);
      const active = (r.data.jobs || []).some((j) => j.status === 'queued' || j.status === 'processing');
      clearTimeout(timer.current);
      if (active) timer.current = setTimeout(load, 5000);
    } catch (_e) { setJobs([]); }
  }, []);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  const submit = async () => {
    if (prompt.trim().length < 5) { toast.error('内容を入力してください（5文字以上）'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/ai-docs/generate`, {
        prompt: prompt.trim(), title: title.trim() || null, formats,
        language: lang, include_diagrams: diagrams,
        idempotency_key: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`),
      }, { withCredentials: true });
      if (r.data.status === 'failed') toast.error(r.data.safe_failure_message || '生成を開始できませんでした');
      else { toast.success('文書生成を開始しました'); setPrompt(''); }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '文書生成に失敗しました');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full" data-testid="docgen-panel">
      <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-4 space-y-3">
        <p className="text-[12px] font-bold text-violet-800 flex items-center gap-1.5">
          <Sparkle size={14} weight="fill" /> 文書生成モード — ローカルAIがPDF / Word文書を作成します
        </p>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
          placeholder="文書タイトル（任意）" data-testid="docgen-title-input"
          className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} maxLength={8000}
          placeholder="例: 製造工程の概要レポートを作成してください。工程を説明する図も含めてください。"
          data-testid="docgen-prompt-input"
          className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500" />
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-zinc-200 bg-white overflow-hidden">
            {FMT_OPTS.map((o) => (
              <button key={o.label} type="button" onClick={() => setFormats(o.v)}
                data-testid={`docgen-format-${o.label}`}
                className={`px-3 py-1.5 text-[11.5px] font-semibold ${JSON.stringify(formats) === JSON.stringify(o.v) ? 'bg-violet-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-zinc-200 bg-white overflow-hidden">
            {LANG_OPTS.map((o) => (
              <button key={o.v} type="button" onClick={() => setLang(o.v)}
                data-testid={`docgen-lang-${o.v}`}
                className={`px-2.5 py-1.5 text-[11.5px] font-semibold ${lang === o.v ? 'bg-violet-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}>
                {o.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-[11.5px] text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={diagrams} onChange={(e) => setDiagrams(e.target.checked)}
              data-testid="docgen-diagrams-checkbox"
              className="rounded border-zinc-300 text-violet-600 focus:ring-violet-500" />
            有用な場合に図を含める
          </label>
          <button type="button" onClick={submit} disabled={submitting || prompt.trim().length < 5}
            data-testid="docgen-generate-btn"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 text-[12px] font-bold disabled:opacity-50">
            {submitting ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
            文書を生成
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="docgen-jobs-list">
        <p className="text-[11px] font-semibold text-zinc-500">生成した文書</p>
        {jobs === null && <p className="text-[11px] text-zinc-400">読み込み中…</p>}
        {jobs !== null && jobs.length === 0 && (
          <p className="text-[11px] text-zinc-400" data-testid="docgen-empty">まだ生成した文書はありません。</p>
        )}
        {(jobs || []).map((j) => <JobCard key={j.id} job={j} onChanged={load} />)}
      </div>
    </div>
  );
}
